import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { money, fmtDate, fmtHours } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { FileDown, Receipt, Upload, CheckCircle2, QrCode, Copy, ExternalLink } from "lucide-react";

// Public invoice page — no auth needed
export default function PublicInvoice() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [payerName, setPayerName] = useState("");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const base = process.env.REACT_APP_BACKEND_URL + "/api";
        const r = await fetch(`${base}/public/invoice/${token}`);
        if (!r.ok) throw new Error("Link inválido");
        setData(await r.json());
      } catch (e) { setErr(e.message); }
    })();
  }, [token]);

  const dl = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/public/invoice/${token}/pdf`;
    window.open(url, "_blank");
  };

  // Correção PIX Asaas, itens 3/4/5 — a página pública deixa de ser só
  // informativa e passa a funcionar como página de PAGAMENTO quando existe
  // um instrumento REAL (Mercado Pago/Asaas) ativo: QR/copia-e-cola do PIX
  // (dados exatos já devolvidos pelo provedor, nunca gerados aqui) ou o
  // link REAL do boleto do provedor (nunca um PDF interno do LOOTUS).
  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado`);
    } catch {
      toast.error(`Não foi possível copiar ${label.toLowerCase()}`);
    }
  };
  const PROVIDER_LABEL = { mercado_pago: "Mercado Pago", asaas: "Asaas" };
  const isIntegratedInstrument = (b) => b.modo === "mercado_pago" || b.modo === "asaas";
  const isSandbox = (b) => b.environment === "test" || b.environment === "sandbox";

  const readFileAsDataURL = (f) => new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(f);
  });

  const uploadReceipt = async () => {
    if (!payerName.trim()) return toast.error("Informe seu nome");
    if (!file) return toast.error("Selecione o comprovante");
    if (file.size > 5 * 1024 * 1024) return toast.error("Arquivo maior que 5 MB");
    setUploading(true);
    try {
      const dataUrl = await readFileAsDataURL(file);
      const base = process.env.REACT_APP_BACKEND_URL + "/api";
      const r = await fetch(`${base}/public/invoice/${token}/receipt`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ payer_name: payerName, file_name: file.name, file_data: dataUrl, note }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Falha ao enviar"); }
      setUploaded(true); toast.success("Comprovante enviado. Obrigado!");
    } catch (e) { toast.error(e.message); }
    finally { setUploading(false); }
  };

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-md bg-white rounded-xl border border-red-200 p-8 shadow-sm text-center">
        <h1 className="font-heading text-xl font-bold text-red-700">Link inválido</h1>
        <p className="text-slate-600 text-sm mt-2">{err}</p>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-slate-500">Carregando...</div>;

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-4">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-blue-600 text-white flex items-center justify-center font-heading font-bold">R</div>
            <div>
              <div className="font-heading font-bold text-slate-900">{data.empresa?.nome || "Relatório de Serviços Prestados"}</div>
              <div className="text-xs text-slate-500">CNPJ {data.empresa?.cnpj || "-"} · {data.empresa?.email || ""}</div>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-4 flex flex-wrap justify-between items-start gap-4">
            <div>
              <div className="text-xs text-slate-500">Faturamento</div>
              <div className="font-heading font-bold text-2xl">{data.invoice.numero}</div>
              <div className="text-sm text-slate-600 mt-1">Cliente: <b>{data.cliente.razao_social}</b></div>
              <div className="text-xs text-slate-500">{data.cliente.cpf_cnpj || ""}</div>
              <div className="text-xs text-slate-500 mt-1">Emitido em {fmtDate(data.invoice.created_at)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs text-slate-500">Total</div>
              <div className="font-heading font-bold text-3xl text-blue-700">{money(data.invoice.valor_total)}</div>
              <Badge className="mt-2 bg-blue-100 text-blue-800">{data.invoice.status}</Badge>
            </div>
          </div>
        </Card>

        <Card className="p-0 overflow-hidden">
          <div className="p-5 border-b border-slate-200 flex items-center justify-between">
            <div className="font-heading font-semibold">Fichas incluídas</div>
            <Button variant="outline" onClick={dl} data-testid="dl-invoice-pdf"><FileDown size={16} /> Baixar PDF</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-slate-600">
                <tr><th className="py-3 px-4">Ficha</th><th>Data</th><th>Serviço</th><th>Horas fat.</th><th>Valor hora</th><th className="text-right pr-4">Valor</th></tr>
              </thead>
              <tbody>
                {data.tickets.map(t => (
                  <tr key={t.numero} className="border-t border-slate-100">
                    <td className="py-3 px-4 font-medium">#{t.numero}</td>
                    <td>{fmtDate(t.data_servico)}</td>
                    <td>{t.tipo_servico}</td>
                    <td>{fmtHours(t.horas_faturaveis)}</td>
                    <td>{money(t.valor_hora)}</td>
                    <td className="text-right pr-4 font-medium">{money(t.valor_total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 border-t border-slate-200">
                <tr><td colSpan={3} className="py-3 px-4 text-right text-slate-600">Total</td><td className="font-bold">{fmtHours(data.invoice.horas_total)}</td><td></td><td className="text-right pr-4 font-heading font-bold text-blue-700">{money(data.invoice.valor_total)}</td></tr>
              </tfoot>
            </table>
          </div>
        </Card>

        {(() => {
          // Etapa 4D (item 8 do pedido) — agrupa visualmente PIX e BOLETOS
          // (relevante para pagamento misto: 1 PIX + N parcelas de
          // boleto); entrada continua com seu próprio rótulo. Nunca ordena
          // pela posição bruta de `data.boletos` (PIX comp1 e a 1ª parcela
          // do boleto comp2 podem empatar em parcela_numero=1 no backend —
          // ver server.py) — agrupa por tipo primeiro, sempre. Faturas
          // antigas (só boleto, ou só PIX) renderizam exatamente como
          // antes: 1 grupo, sem cabeçalho (só aparece com os 2 grupos
          // presentes, o caso realmente novo aqui).
          const todos = data.boletos || [];
          const entradaItems = todos.filter(b => b.tipo_cobranca === "entrada");
          const pixItems = todos.filter(b => b.tipo_cobranca !== "entrada" && b.instrumento_tipo === "pix");
          const boletoItems = todos.filter(b => b.tipo_cobranca !== "entrada" && b.instrumento_tipo !== "pix")
            .sort((a, b) => (a.parcela_numero || 0) - (b.parcela_numero || 0));
          const agrupado = pixItems.length > 0 && boletoItems.length > 0;
          // Termos amigáveis (item 8) — nunca expõe "comp1"/"comp2" ao
          // cliente, nem no título nem no número do documento (o
          // numero_documento gerado pelo backend para um componente do
          // misto embute o component_id — ver _create_componente_boletos
          // em server.py; component_id é usado SÓ para organizar aqui,
          // nunca exibido literalmente).
          const chargeTitle = (b) => {
            if (b.tipo_cobranca === "entrada") return "Entrada";
            if (b.instrumento_tipo === "pix") return "PIX";
            if (b.parcela_numero) return `Boleto ${b.parcela_numero}/${b.parcela_total}`;
            return "Boleto";
          };
          const docLabel = (b) => b.component_id ? `${data.invoice.numero}-${b.parcela_numero}/${b.parcela_total}` : b.numero_documento;

          const renderCard = (b) => (
            <Card className="p-5" key={b.numero_documento}>
              {b.instrumento_tipo === "pix" && isIntegratedInstrument(b) ? (
                <>
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <div className="flex items-center gap-2"><QrCode size={18} className="text-blue-600" /><div className="font-heading font-semibold">Pague via {chargeTitle(b)}</div></div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-800">{PROVIDER_LABEL[b.modo] || b.modo}</Badge>
                      {isSandbox(b) && <Badge className="bg-amber-100 text-amber-800">Ambiente de teste</Badge>}
                    </div>
                  </div>
                  <div className="text-center space-y-3">
                    {b.pix_qr_code_base64 ? (
                      <img src={`data:image/png;base64,${b.pix_qr_code_base64}`} alt="QR Code PIX" className="mx-auto w-56 h-56 border border-slate-200 rounded-md" data-testid="public-pix-qrcode" />
                    ) : (
                      <p className="text-sm text-slate-500">QR Code indisponível — use o Copia e Cola abaixo.</p>
                    )}
                    <div className="text-left text-sm bg-slate-50 border border-slate-200 rounded-md p-3 space-y-1 max-w-sm mx-auto">
                      <div><span className="text-slate-500">Valor:</span> <span className="font-medium text-slate-900">{money(b.valor)}</span></div>
                      <div><span className="text-slate-500">Vencimento:</span> <span className="font-medium text-slate-900">{fmtDate(b.vencimento)}</span></div>
                      <div><span className="text-slate-500">Status:</span> <span className="font-medium text-slate-900">{b.status}</span></div>
                    </div>
                    {b.pix_qr_code && (
                      <div className="max-w-sm mx-auto text-left">
                        <div className="text-xs text-slate-500 mb-1">PIX Copia e Cola</div>
                        <div className="font-mono text-xs bg-slate-50 border border-slate-200 rounded-md p-3 break-all" data-testid="public-pix-copia-cola">{b.pix_qr_code}</div>
                      </div>
                    )}
                    {b.pix_qr_code && (
                      <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => copyToClipboard(b.pix_qr_code, "Código PIX")} data-testid="public-pix-copiar">
                        <Copy size={16} /> Copiar código PIX
                      </Button>
                    )}
                  </div>
                </>
              ) : b.instrumento_tipo === "boleto" && isIntegratedInstrument(b) ? (
                <>
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <div className="flex items-center gap-2"><Receipt size={18} className="text-blue-600" /><div className="font-heading font-semibold">{chargeTitle(b)}</div></div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-blue-100 text-blue-800">{PROVIDER_LABEL[b.modo] || b.modo}</Badge>
                      {isSandbox(b) && <Badge className="bg-amber-100 text-amber-800">Ambiente de teste</Badge>}
                    </div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <div className="flex flex-wrap justify-between gap-3 text-sm">
                      <div><b>Doc {docLabel(b)}</b> · Vencimento {fmtDate(b.vencimento)}</div>
                      <div><Badge className="bg-blue-100 text-blue-800">{b.status}</Badge> · <b>{money(b.valor)}</b></div>
                    </div>
                    {b.linha_digitavel && <div className="mt-2 font-mono text-xs text-slate-600 break-all">{b.linha_digitavel}</div>}
                  </div>
                  {/* Item 4 — SEMPRE a URL REAL do boleto no provedor (bankSlipUrl/
                      boleto_url), nunca um PDF interno do LOOTUS no lugar dele. */}
                  {b.boleto_url && (
                    <Button className="mt-3 bg-blue-600 hover:bg-blue-700" onClick={() => window.open(b.boleto_url, "_blank", "noopener,noreferrer")} data-testid="public-abrir-boleto">
                      <ExternalLink size={16} /> Abrir boleto
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2 mb-3"><Receipt size={18} className="text-blue-600" /><div className="font-heading font-semibold">{chargeTitle(b)}</div></div>
                  <div className="p-3 bg-slate-50 rounded-md border border-slate-200">
                    <div className="flex flex-wrap justify-between gap-3 text-sm">
                      <div><b>Doc {docLabel(b)}</b></div>
                      {b.modo === "simulado" && <Badge className="bg-amber-100 text-amber-800">Modo simulado</Badge>}
                    </div>
                    <div className="mt-2 font-mono text-xs text-slate-600 break-all">{b.linha_digitavel}</div>
                  </div>
                </>
              )}
            </Card>
          );

          return (
            <>
              {entradaItems.map(renderCard)}
              {agrupado && pixItems.length > 0 && (
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1" data-testid="public-grupo-pix">PIX</div>
              )}
              {pixItems.map(renderCard)}
              {agrupado && boletoItems.length > 0 && (
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-1" data-testid="public-grupo-boletos">Boletos</div>
              )}
              {boletoItems.map(renderCard)}
            </>
          );
        })()}

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3"><Upload size={18} className="text-blue-600" /><div className="font-heading font-semibold">Anexar comprovante de pagamento</div></div>
          {uploaded ? (
            <div className="text-center py-4" data-testid="receipt-uploaded">
              <CheckCircle2 className="mx-auto text-emerald-600" size={40} />
              <p className="mt-2 text-slate-700 font-medium">Comprovante enviado com sucesso</p>
              <p className="text-sm text-slate-500">O financeiro foi notificado e vai confirmar seu pagamento.</p>
              <Button variant="outline" size="sm" className="mt-3" onClick={() => { setUploaded(false); setFile(null); setNote(""); }}>Enviar outro</Button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">Se você já efetuou o pagamento, anexe aqui o comprovante (PDF ou imagem, máx 5 MB). O financeiro receberá um aviso automático.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div><Label>Seu nome</Label><Input value={payerName} onChange={e => setPayerName(e.target.value)} data-testid="receipt-payer" /></div>
                <div>
                  <Label>Arquivo (PDF/imagem)</Label>
                  <Input type="file" accept="application/pdf,image/*" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="receipt-file" />
                  {file && <div className="text-xs text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(0)} KB</div>}
                </div>
              </div>
              <div><Label>Observação (opcional)</Label><Textarea rows={2} value={note} onChange={e => setNote(e.target.value)} data-testid="receipt-note" placeholder="Ex.: pago via PIX às 14h de hoje..." /></div>
              <Button className="bg-blue-600 hover:bg-blue-700 w-full md:w-auto" disabled={uploading} onClick={uploadReceipt} data-testid="receipt-submit">
                <Upload size={16} /> {uploading ? "Enviando..." : "Enviar comprovante"}
              </Button>
            </div>
          )}
        </Card>

        <div className="text-center text-xs text-slate-400">© {new Date().getFullYear()} · {data.empresa?.nome}</div>
      </div>
    </div>
  );
}
